import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Landmark, Package } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { VALIDITY } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pill } from "@/components/documents/AgreementsTable";

const saveBlob = (data, name) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); };

export const PackDialog = ({ open, onOpenChange, documentIds, isoIds, canBank, canReveal }) => {
  const [preview, setPreview] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [sheetBank, setSheetBank] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setSheet(false); setSheetBank(false); setPreview(null);
    api.post("/onboarding/pack/preview", { document_ids: documentIds, iso_ids: isoIds }).then((r) => setPreview(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, documentIds, isoIds]);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/onboarding/pack", { document_ids: documentIds, iso_ids: isoIds, include_details_sheet: sheet, include_bank_in_sheet: sheet && sheetBank }, { responseType: "blob" });
      saveBlob(res.data, `MarksMan-Onboarding-Pack-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.zip`);
      toast.success("Onboarding pack downloaded"); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const items = preview?.items || [];
  const noFile = items.filter((i) => i.type === "iso" && !i.has_file);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="pack-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="h-4 w-4 text-teal-600" /> Onboarding pack</DialogTitle><DialogDescription>One ZIP with the selected files and a manifest. Only what you ticked is included.</DialogDescription></DialogHeader>
        {!preview ? <div className="text-sm text-slate-500 py-6 text-center">Preparing preview…</div> : (
          <div className="space-y-3">
            <ul className="divide-y border rounded-md" data-testid="pack-items">
              {items.map((i) => (
                <li key={i.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`pack-item-${i.id}`}>
                  <div className="min-w-0"><div className="font-medium text-slate-800 flex items-center gap-1.5">{i.is_bank && <Landmark className="h-3.5 w-3.5 text-amber-600" />}{i.name}</div><div className="text-xs text-slate-500">{i.category} · {i.filename || "no file"}</div></div>
                  <div className="flex items-center gap-2">{i.type === "iso" && !i.has_file && <span className="text-[11px] text-rose-700">No file — skipped</span>}<Pill meta={VALIDITY[i.validity]} /></div>
                </li>
              ))}
              {items.length === 0 && <li className="px-3 py-3 text-sm text-slate-500">No documents selected.</li>}
            </ul>
            {preview.warnings.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex gap-2" data-testid="pack-warnings"><AlertTriangle className="h-4 w-4 shrink-0" /><div>{preview.warnings.map((w, i) => <div key={i}>{w}</div>)}<div className="mt-1 font-medium">Consider replacing these before sending to a client.</div></div></div>
            )}
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="pack-include-sheet" checked={sheet} onCheckedChange={(v) => setSheet(!!v)} /> Include Company details sheet (PDF)</label>
              {sheet && canBank && <label className="flex items-center gap-2 text-sm ml-6"><Checkbox data-testid="pack-include-bank" checked={sheetBank} onCheckedChange={(v) => setSheetBank(!!v)} disabled={!canReveal} /> Include bank account details in the sheet{!canReveal && <span className="text-xs text-slate-400">(Admin / Finance only)</span>}</label>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="button" data-testid="pack-download-button" className="bg-teal-600 hover:bg-teal-700" disabled={busy || (items.length - noFile.length === 0 && !sheet)} onClick={download}>{busy ? "Building…" : `Download ZIP (${items.length - noFile.length + (sheet ? 1 : 0)} file${items.length - noFile.length + (sheet ? 1 : 0) === 1 ? "" : "s"})`}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
